import { useState } from "react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, Instagram, Clock } from "lucide-react";
import { waMessage } from "@/components/WhatsAppButton";
import { toast } from "sonner";

const Contact = () => {
  const { settings, social } = useStore();
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const tel = `tel:${(settings.phone || "").replace(/\s/g, "")}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.subject || !form.message) { toast.error("Please fill all required fields"); return; }
    setLoading(true);
    try { await api.post("/contact", form); toast.success("Message sent! We'll get back to you soon."); setForm({ name: "", phone: "", email: "", subject: "", message: "" }); }
    catch { toast.error("Failed to send. Please try WhatsApp."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-beige/50 py-14 text-center">
        <h1 className="font-serif text-5xl text-burgundy">Get In Touch</h1>
        <p className="mt-2 text-muted-text">We'd love to hear from you</p>
      </section>
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-16 md:grid-cols-2">
        <div>
          <h2 className="font-serif text-2xl text-charcoal">Visit Our Store</h2>
          <div className="mt-6 space-y-5 text-sm">
            <p className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-burgundy" /><span>{settings.address || settings.location}</span></p>
            <a href={tel} className="flex items-center gap-3 hover:text-burgundy" data-testid="contact-phone"><Phone className="h-5 w-5 text-burgundy" />{settings.phone}</a>
            {settings.email && <a href={`mailto:${settings.email}`} className="flex items-center gap-3 hover:text-burgundy"><Mail className="h-5 w-5 text-burgundy" />{settings.email}</a>}
            <a href={social.instagram || "https://www.instagram.com/uniqueclothespalnpur/?hl=en"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-burgundy" data-testid="contact-instagram"><Instagram className="h-5 w-5 text-burgundy" />Follow us on Instagram</a>
            <p className="flex items-center gap-3"><Clock className="h-5 w-5 text-burgundy" />Monday – Sunday · 10:00 AM – 9:00 PM</p>
          </div>
          <div className="mt-8 flex gap-3">
            <Button asChild className="rounded-none bg-burgundy"><a href={tel}>Call Us</a></Button>
            <Button asChild variant="outline" className="rounded-none border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white"><a href={waMessage(settings)} target="_blank" rel="noopener noreferrer">WhatsApp Us</a></Button>
          </div>
        </div>

        <div className="border border-border p-8">
          <h2 className="font-serif text-2xl text-charcoal">Send Us a Message</h2>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label>Your Name *</Label><Input value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-1 rounded-none" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Mobile *</Label><Input value={form.phone} onChange={set("phone")} data-testid="contact-mobile" className="mt-1 rounded-none" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="mt-1 rounded-none" /></div>
            </div>
            <div><Label>Subject *</Label><Input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-1 rounded-none" /></div>
            <div><Label>Message *</Label><Textarea value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-1 rounded-none" rows={4} /></div>
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full rounded-none bg-burgundy uppercase tracking-widest">{loading ? "Sending..." : "Send Message"}</Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
